import React from 'react';
import { Sparkle, Image as ImageIcon } from '@phosphor-icons/react';
import Modal from '@Common/Modal';

interface PromptInstructionsProps {
  isOpen: boolean;
  onClose: () => void;
}

const PromptInstructions: React.FC<PromptInstructionsProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="How to Prompt for Image Generation"
    >
      <div className="space-y-4 text-sm text-gray-700">
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">
            <strong>Important:</strong> To generate text within images, enclose
            the text in double quotes (e.g., "Hello World"). Note: Due to model
            limitations, text output may be inconsistent or malformed.
          </p>
        </div>

        <p className="text-gray-600">
          Get the best results by understanding how to prompt for different
          image operations:
        </p>

        <div className="space-y-3">
          <div className="rounded-lg bg-blue-50 p-3">
            <h4 className="mb-1 flex items-center gap-2 font-semibold text-blue-900">
              <Sparkle size={16} weight="fill" />
              Text-to-Image (No Upload)
            </h4>
            <p className="mb-2 text-xs text-blue-800">
              Generate new images from scratch with descriptive prompts.
            </p>
            <div className="space-y-1 text-xs">
              <p className="font-medium text-blue-900">Examples:</p>
              <p className="italic text-blue-700">
                "A serene mountain landscape at sunset with pine trees"
              </p>
              <p className="italic text-blue-700">
                "Modern minimalist logo for a tech startup"
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-purple-50 p-3">
            <h4 className="mb-1 flex items-center gap-2 font-semibold text-purple-900">
              <ImageIcon size={16} weight="fill" />
              Image Variation (Upload Image)
            </h4>
            <p className="mb-2 text-xs text-purple-800">
              Modify the overall style, colors, or mood of your uploaded image.
            </p>
            <div className="space-y-1 text-xs">
              <p className="font-medium text-purple-900">Examples:</p>
              <p className="italic text-purple-700">
                "Make it brighter and more vibrant"
              </p>
              <p className="italic text-purple-700">
                "Change style to watercolor painting"
              </p>
              <p className="italic text-purple-700">
                "Apply vintage film filter"
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-green-50 p-3">
            <h4 className="mb-1 flex items-center gap-2 font-semibold text-green-900">
              <Sparkle size={16} weight="fill" />
              Background Removal (Upload Image)
            </h4>
            <p className="mb-2 text-xs text-green-800">
              Remove or make the background transparent.
            </p>
            <div className="space-y-1 text-xs">
              <p className="font-medium text-green-900">Examples:</p>
              <p className="italic text-green-700">"Remove background"</p>
              <p className="italic text-green-700">"Transparent background"</p>
              <p className="italic text-green-700">"Isolate the subject"</p>
            </div>
          </div>

          <div className="rounded-lg bg-pink-50 p-3">
            <h4 className="mb-1 flex items-center gap-2 font-semibold text-pink-900">
              <ImageIcon size={16} weight="fill" />
              Outpainting (Upload Image)
            </h4>
            <p className="mb-2 text-xs text-pink-800">
              Change or replace the entire background scene.
            </p>
            <div className="space-y-1 text-xs">
              <p className="font-medium text-pink-900">Examples:</p>
              <p className="italic text-pink-700">
                "Change background to a beach sunset"
              </p>
              <p className="italic text-pink-700">
                "Place this in a modern office"
              </p>
              <p className="italic text-pink-700">
                "Replace background with mountains"
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-gray-100 p-3">
          <h4 className="mb-1 font-semibold text-gray-900">
            Tip: Document Support
          </h4>
          <p className="text-xs text-gray-700">
            Upload PDF, TXT, or Markdown files to extract visual concepts and
            generate images based on document content!
          </p>
        </div>

        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
          <h4 className="text-primary-900 mb-1 font-semibold"> Pro Tips</h4>
          <ul className="text-primary-800 list-inside list-disc space-y-1 text-xs">
            <li>Be specific and descriptive in your prompts</li>
            <li>Mention style, mood, colors, and composition</li>
            <li>For editing, clearly state what you want to change</li>
            <li>You have 100 generations per hour, 500 per day</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default PromptInstructions;
